import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, UserPlus, UserMinus, Users, FileText, Check, X, Loader, Download, Database, Upload, Trash2, RefreshCw, ClipboardList } from 'lucide-react';
import { getBetaUsers, addBetaUser, removeBetaUser, getPendingQuotes, reviewQuote, getQuoteDownloadUrl } from './firebase';
import { seedAllPricingData, getCurrentCounts, getLocalDataStats, clearSoftwareData } from './services/seedPricingData';
import { getAuditLog } from './services/db';
import { SkeletonQuoteRow } from './components/SkeletonCard';
import './AdminPanel.css';

const QUOTES_PAGE_SIZE = 20;

const AdminPanel = () => {
  const navigate = useNavigate();
  const { isAdmin } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState('beta-users');
  const [betaUsers, setBetaUsers] = useState([]);
  const [pendingQuotes, setPendingQuotes] = useState([]);
  const [displayedQuotes, setDisplayedQuotes] = useState([]);
  const [quotePage, setQuotePage] = useState(1);
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // DEV-17: Bulk selection
  const [selectedQuoteIds, setSelectedQuoteIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState('');

  // DEV-18: Audit log
  const [auditLog, setAuditLog] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  // Database seeding state
  const [dbCounts, setDbCounts] = useState({ categories: 0, software: 0 });
  const [localStats, setLocalStats] = useState(null);
  const [seedProgress, setSeedProgress] = useState([]);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    if (!isAdmin) navigate('/dashboard');
  }, [isAdmin, navigate]);

  useEffect(() => {
    loadData();
  }, []);

  // Paginate displayed quotes when pendingQuotes or quotePage changes
  useEffect(() => {
    setDisplayedQuotes(pendingQuotes.slice(0, quotePage * QUOTES_PAGE_SIZE));
  }, [pendingQuotes, quotePage]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [betaResult, quotesResult] = await Promise.all([
        getBetaUsers(),
        getPendingQuotes()
      ]);
      setBetaUsers(betaResult.data.betaUsers || []);
      setPendingQuotes(quotesResult.data.quotes || []);
      setQuotePage(1);
    } catch (err) {
      console.error('Error loading admin data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadDatabaseStats = async () => {
    try {
      const [counts, stats] = await Promise.all([
        getCurrentCounts(),
        Promise.resolve(getLocalDataStats())
      ]);
      setDbCounts(counts);
      setLocalStats(stats);
    } catch (err) {
      console.error('Error loading database stats:', err);
    }
  };

  // DEV-18: Load audit log
  const loadAuditLog = useCallback(async () => {
    setAuditLoading(true);
    try {
      const entries = await getAuditLog();
      setAuditLog(entries);
    } catch (err) {
      console.error('Error loading audit log:', err);
      setError('Failed to load audit log');
    } finally {
      setAuditLoading(false);
    }
  }, []);

  // Load tab-specific data when switching tabs
  useEffect(() => {
    if (activeTab === 'database') loadDatabaseStats();
    if (activeTab === 'audit') loadAuditLog();
  }, [activeTab, loadAuditLog]);

  const handleSeedDatabase = async () => {
    if (!window.confirm('This will upload all scraped pricing data to Firestore. Existing data will be merged/updated. Continue?')) return;
    setSeeding(true);
    setSeedProgress([]);
    setError('');
    setSuccess('');
    try {
      const result = await seedAllPricingData((msg) => setSeedProgress(prev => [...prev, msg]));
      setSuccess(`Successfully seeded ${result.categoryCount} categories and ${result.softwareCount} software products!`);
      await loadDatabaseStats();
    } catch (err) {
      setError(err.message || 'Failed to seed database');
    } finally {
      setSeeding(false);
    }
  };

  const handleClearDatabase = async () => {
    if (!window.confirm('WARNING: This will delete ALL software data from Firestore. This cannot be undone. Are you sure?')) return;
    if (!window.confirm('Are you REALLY sure? Type "DELETE" to confirm.')) return;
    setSeeding(true);
    setSeedProgress([]);
    setError('');
    setSuccess('');
    try {
      const deleted = await clearSoftwareData((msg) => setSeedProgress(prev => [...prev, msg]));
      setSuccess(`Deleted ${deleted} software products from Firestore.`);
      await loadDatabaseStats();
    } catch (err) {
      setError(err.message || 'Failed to clear database');
    } finally {
      setSeeding(false);
    }
  };

  const handleAddBetaUser = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setActionLoading(true);
    setError('');
    setSuccess('');
    try {
      await addBetaUser({ email: newEmail.trim() });
      setSuccess(`Added ${newEmail} to beta users`);
      setNewEmail('');
      const result = await getBetaUsers();
      setBetaUsers(result.data.betaUsers || []);
    } catch (err) {
      setError(err.message || 'Failed to add user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveBetaUser = async (email) => {
    if (!window.confirm(`Remove ${email} from beta access?`)) return;
    setActionLoading(true);
    setError('');
    setSuccess('');
    try {
      await removeBetaUser({ email });
      setSuccess(`Removed ${email} from beta users`);
      const result = await getBetaUsers();
      setBetaUsers(result.data.betaUsers || []);
    } catch (err) {
      setError(err.message || 'Failed to remove user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReviewQuote = async (quoteId, status) => {
    setActionLoading(true);
    setError('');
    setSuccess('');
    try {
      await reviewQuote({ quoteId, status });
      setSuccess(`Quote ${status}`);
      setSelectedQuoteIds(prev => { const next = new Set(prev); next.delete(quoteId); return next; });
      const result = await getPendingQuotes();
      setPendingQuotes(result.data.quotes || []);
      setQuotePage(1);
    } catch (err) {
      setError(err.message || 'Failed to review quote');
    } finally {
      setActionLoading(false);
    }
  };

  // DEV-17: Bulk review
  const handleBulkAction = async (status) => {
    if (selectedQuoteIds.size === 0) return;
    const count = selectedQuoteIds.size;
    if (!window.confirm(`${status === 'approved' ? 'Approve' : 'Reject'} ${count} selected quote${count > 1 ? 's' : ''}?`)) return;

    setBulkLoading(true);
    setBulkProgress(`Processing 0 / ${count}...`);
    setError('');
    setSuccess('');

    let done = 0;
    const ids = [...selectedQuoteIds];
    try {
      await Promise.all(ids.map(async (id) => {
        await reviewQuote({ quoteId: id, status });
        done++;
        setBulkProgress(`Processing ${done} / ${count}...`);
      }));
      setSuccess(`${done} quote${done > 1 ? 's' : ''} ${status}.`);
      setSelectedQuoteIds(new Set());
      const result = await getPendingQuotes();
      setPendingQuotes(result.data.quotes || []);
      setQuotePage(1);
    } catch (err) {
      setError(err.message || 'Bulk action failed');
    } finally {
      setBulkLoading(false);
      setBulkProgress('');
    }
  };

  const handleDownloadQuote = async (filePath) => {
    setError('');
    try {
      const result = await getQuoteDownloadUrl({ filePath });
      window.open(result.data.url, '_blank');
    } catch (err) {
      setError(err.message || 'Failed to download quote file');
    }
  };

  // DEV-17: Select all toggle
  const allSelected = displayedQuotes.length > 0 && displayedQuotes.every(q => selectedQuoteIds.has(q.id));
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedQuoteIds(new Set());
    } else {
      setSelectedQuoteIds(new Set(displayedQuotes.map(q => q.id)));
    }
  };

  const toggleQuote = (id) => {
    setSelectedQuoteIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="admin-panel">
        <div className="admin-loading">
          <Loader className="spin" size={24} />
          <span>Loading admin panel...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <header className="admin-header">
        <button onClick={() => navigate('/dashboard')} className="admin-back-btn">
          <ArrowLeft size={20} />
          Back
        </button>
        <nav className="admin-nav">
          <button className={`admin-nav-btn ${activeTab === 'beta-users' ? 'active' : ''}`} onClick={() => setActiveTab('beta-users')}>
            <Users size={18} />
            Beta Users
          </button>
          <button className={`admin-nav-btn ${activeTab === 'quotes' ? 'active' : ''}`} onClick={() => setActiveTab('quotes')}>
            <FileText size={18} />
            Quotes
            {pendingQuotes.length > 0 && <span className="admin-badge">{pendingQuotes.length}</span>}
          </button>
          <button className={`admin-nav-btn ${activeTab === 'database' ? 'active' : ''}`} onClick={() => setActiveTab('database')}>
            <Database size={18} />
            Database
          </button>
          <button className={`admin-nav-btn ${activeTab === 'audit' ? 'active' : ''}`} onClick={() => setActiveTab('audit')}>
            <ClipboardList size={18} />
            Audit Log
          </button>
        </nav>
        <div className="admin-header-spacer"></div>
      </header>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}
      {success && <div className="admin-alert admin-alert-success">{success}</div>}

      <div className="admin-content">

        {/* BETA USERS TAB */}
        {activeTab === 'beta-users' && (
          <div className="admin-section">
            <div className="admin-section-header">
              <h2>Manage Beta Users</h2>
              <p>Add or remove users from the beta program</p>
            </div>
            <form onSubmit={handleAddBetaUser} className="admin-add-form">
              <input
                type="email"
                placeholder="Enter email address"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="admin-input"
                disabled={actionLoading}
              />
              <button type="submit" className="admin-btn admin-btn-primary" disabled={actionLoading}>
                <UserPlus size={18} />
                Add User
              </button>
            </form>
            <div className="admin-list">
              <h3>Current Beta Users ({betaUsers.length})</h3>
              {betaUsers.length === 0 ? (
                <p className="admin-empty">No beta users yet</p>
              ) : (
                <ul className="admin-user-list">
                  {betaUsers.map((user) => (
                    <li key={user.email} className="admin-user-item">
                      <div className="admin-user-info">
                        <span className="admin-user-email">{user.email}</span>
                        {(user.source === 'admin' || user.source === 'config') && (
                          <span className="admin-user-badge">{user.source === 'admin' ? 'Admin' : 'Config'}</span>
                        )}
                      </div>
                      {user.source !== 'admin' && user.source !== 'config' && (
                        <button onClick={() => handleRemoveBetaUser(user.email)} className="admin-btn admin-btn-danger" disabled={actionLoading}>
                          <UserMinus size={16} />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* QUOTES TAB */}
        {activeTab === 'quotes' && (
          <div className="admin-section">
            <div className="admin-section-header">
              <h2>Quote Review</h2>
              <p>Review and approve submitted pricing quotes</p>
            </div>

            {pendingQuotes.length === 0 ? (
              <div className="admin-empty-state">
                <FileText size={48} />
                <h3>No Pending Quotes</h3>
                <p>All quotes have been reviewed. New submissions will appear here.</p>
              </div>
            ) : (
              <>
                {/* DEV-17: Bulk action bar */}
                <div className="admin-bulk-bar">
                  <label className="admin-bulk-check">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                    />
                    <span>Select all ({displayedQuotes.length})</span>
                  </label>
                  {selectedQuoteIds.size > 0 && (
                    <div className="admin-bulk-actions">
                      <span className="admin-bulk-count">{selectedQuoteIds.size} selected</span>
                      <button
                        className="admin-btn admin-btn-success"
                        onClick={() => handleBulkAction('approved')}
                        disabled={bulkLoading || actionLoading}
                      >
                        {bulkLoading ? <Loader className="spin" size={16} /> : <Check size={16} />}
                        Approve Selected
                      </button>
                      <button
                        className="admin-btn admin-btn-danger"
                        onClick={() => handleBulkAction('rejected')}
                        disabled={bulkLoading || actionLoading}
                      >
                        {bulkLoading ? <Loader className="spin" size={16} /> : <X size={16} />}
                        Reject Selected
                      </button>
                    </div>
                  )}
                  {bulkProgress && <span className="admin-bulk-progress">{bulkProgress}</span>}
                </div>

                <div className="admin-quotes-list">
                  {displayedQuotes.map((quote) => (
                    <div key={quote.id} className={`admin-quote-card ${selectedQuoteIds.has(quote.id) ? 'selected' : ''}`}>
                      <div className="admin-quote-header">
                        <div className="admin-quote-header-left">
                          <input
                            type="checkbox"
                            className="admin-quote-checkbox"
                            checked={selectedQuoteIds.has(quote.id)}
                            onChange={() => toggleQuote(quote.id)}
                          />
                          <h4>{quote.softwareName}</h4>
                        </div>
                        <span className="admin-quote-date">
                          {quote.submittedAt ? new Date(quote.submittedAt).toLocaleDateString() : 'Unknown date'}
                        </span>
                      </div>
                      <div className="admin-quote-details">
                        <div className="admin-quote-row"><span>Total Price:</span><strong>${quote.data?.totalPrice || 'N/A'}</strong></div>
                        <div className="admin-quote-row"><span>Seats:</span><strong>{quote.data?.seats || 'N/A'}</strong></div>
                        <div className="admin-quote-row"><span>Term:</span><strong>{quote.data?.term || 'N/A'}</strong></div>
                        <div className="admin-quote-row"><span>Tier:</span><strong>{quote.data?.tier || 'N/A'}</strong></div>
                        {quote.data?.notes && (
                          <div className="admin-quote-notes"><span>Notes:</span><p>{quote.data.notes}</p></div>
                        )}
                      </div>
                      <div className="admin-quote-actions">
                        {quote.filePath && (
                          <button onClick={() => handleDownloadQuote(quote.filePath)} className="admin-btn admin-btn-secondary" disabled={actionLoading}>
                            <Download size={18} />
                            View File
                          </button>
                        )}
                        <button onClick={() => handleReviewQuote(quote.id, 'approved')} className="admin-btn admin-btn-success" disabled={actionLoading || bulkLoading}>
                          <Check size={18} />
                          Approve
                        </button>
                        <button onClick={() => handleReviewQuote(quote.id, 'rejected')} className="admin-btn admin-btn-danger" disabled={actionLoading || bulkLoading}>
                          <X size={18} />
                          Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* DEV-11: Pagination for admin quotes */}
                {pendingQuotes.length > displayedQuotes.length && (
                  <div className="admin-load-more">
                    <button
                      className="admin-btn admin-btn-secondary"
                      onClick={() => setQuotePage(p => p + 1)}
                    >
                      Load more ({pendingQuotes.length - displayedQuotes.length} remaining)
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* DATABASE TAB */}
        {activeTab === 'database' && (
          <div className="admin-section">
            <div className="admin-section-header">
              <h2>Database Management</h2>
              <p>Seed and manage software pricing data in Firestore</p>
            </div>
            <div className="admin-db-stats">
              <div className="admin-db-stat-card">
                <h4>Firestore (Live)</h4>
                <div className="admin-db-stat-row"><span>Categories:</span><strong>{dbCounts.categories}</strong></div>
                <div className="admin-db-stat-row"><span>Software Products:</span><strong>{dbCounts.software}</strong></div>
                <button onClick={loadDatabaseStats} className="admin-btn admin-btn-secondary admin-btn-sm" disabled={seeding}>
                  <RefreshCw size={14} />
                  Refresh
                </button>
              </div>
              <div className="admin-db-stat-card">
                <h4>Local Data (pricingData.js)</h4>
                {localStats ? (
                  <>
                    <div className="admin-db-stat-row"><span>Categories:</span><strong>{localStats.totalCategories}</strong></div>
                    <div className="admin-db-stat-row"><span>Software Products:</span><strong>{localStats.totalSoftware}</strong></div>
                    <div className="admin-db-breakdown">
                      {Object.entries(localStats.byCategory).map(([cat, count]) => (
                        <span key={cat} className="admin-db-cat-tag">{cat}: {count}</span>
                      ))}
                    </div>
                  </>
                ) : <p>Loading...</p>}
              </div>
            </div>
            <div className="admin-db-actions">
              <h3>Actions</h3>
              <div className="admin-db-action-btns">
                <button onClick={handleSeedDatabase} className="admin-btn admin-btn-primary" disabled={seeding}>
                  {seeding ? <Loader className="spin" size={18} /> : <Upload size={18} />}
                  Seed Pricing Data
                </button>
                <button onClick={handleClearDatabase} className="admin-btn admin-btn-danger" disabled={seeding}>
                  <Trash2 size={18} />
                  Clear All Software
                </button>
              </div>
              <p className="admin-db-hint">Seeding will merge/update existing documents. Use "Clear" to start fresh.</p>
            </div>
            {seedProgress.length > 0 && (
              <div className="admin-db-progress">
                <h3>Progress</h3>
                <div className="admin-db-log">
                  {seedProgress.map((msg, i) => (
                    <div key={i} className="admin-db-log-entry">{msg}</div>
                  ))}
                </div>
              </div>
            )}
            {localStats && (
              <div className="admin-db-software-list">
                <h3>Available Software ({localStats.totalSoftware})</h3>
                <div className="admin-db-sw-grid">
                  {localStats.softwareList.map((sw) => (
                    <div key={sw.id} className="admin-db-sw-item">
                      <span className="admin-db-sw-name">{sw.name}</span>
                      <span className="admin-db-sw-cat">{sw.category}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* AUDIT LOG TAB (DEV-18) */}
        {activeTab === 'audit' && (
          <div className="admin-section">
            <div className="admin-section-header">
              <h2>Audit Log</h2>
              <p>Last 50 quote review actions by admins</p>
            </div>
            <button onClick={loadAuditLog} className="admin-btn admin-btn-secondary admin-btn-sm" disabled={auditLoading} style={{ marginBottom: 'var(--space-5)' }}>
              <RefreshCw size={14} />
              Refresh
            </button>
            {auditLoading ? (
              <div className="admin-quotes-list">
                {[...Array(5)].map((_, i) => <SkeletonQuoteRow key={i} />)}
              </div>
            ) : auditLog.length === 0 ? (
              <div className="admin-empty-state">
                <ClipboardList size={48} />
                <h3>No Audit Entries Yet</h3>
                <p>Quote review actions will appear here as they happen.</p>
              </div>
            ) : (
              <div className="admin-audit-table">
                <div className="admin-audit-header-row">
                  <span>Action</span>
                  <span>Software</span>
                  <span>Admin</span>
                  <span>Date</span>
                </div>
                {auditLog.map((entry) => (
                  <div key={entry.id} className="admin-audit-row">
                    <span className={`admin-audit-action ${entry.action}`}>
                      {entry.action === 'approved' ? '✓ Approved' : '✗ Rejected'}
                    </span>
                    <span className="admin-audit-software">{entry.softwareName || '—'}</span>
                    <span className="admin-audit-admin">{entry.adminEmail || '—'}</span>
                    <span className="admin-audit-date">
                      {entry.timestamp ? entry.timestamp.toLocaleDateString() : '—'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default AdminPanel;
